import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faBox, faCalendar, faChevronRight, faFileExcel, faSearch, faTags, faUser } from '@fortawesome/free-solid-svg-icons';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { TenantWordsPipe } from '../../../../core/pipes/tenant-words.pipe';
import { InventoryBatchDTO } from '../../models/inventory-batch-dto';
import { InventoryBatchService } from '../../services/inventory-batch.service';
import { InventoryBatchActionsComponent } from '../../widgets/inventory-batch-actions/inventory-batch-actions.component';
import { KW_QUANTITY_FORMAT } from '../../../../../@core/utils/ejs-constants';

const MOBILE_PAGE_SIZE = 25;

/** Flat grid row; the list only shows a batch's first detail, as before. */
interface BatchRow {
  batch: InventoryBatchDTO;
  id?: number;
  lotNumber: string;
  itemName: string;
  workOrderType: string;
  workOrderTypeColor?: string;
  createdBy: string;
  createdDateObj: Date | null;
  availableQuantity?: number;
}

@Component({
  selector: 'inventory-batch-list',
  templateUrl: './inventory-batch-list.component.html',
  styleUrls: ['./inventory-batch-list.component.scss']
})
export class InventoryBatchListComponent implements OnInit {
  @ViewChild('typeTemplate', { static: true }) public typeTemplate: any;
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;
  @ViewChild('grid', { static: false }) grid: GridComponent;

  public faBox = faBox;
  public faCalendar = faCalendar;
  public faChevronRight = faChevronRight;
  public faFileExcel = faFileExcel;
  public faSearch = faSearch;
  public faTags = faTags;
  public faUser = faUser;

  public isLoading = true;
  public searchTerm = '';
  public rows: BatchRow[] = [];
  private allRows: BatchRow[] = [];
  public columns: ColumnModel[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public pageSettings: PageSettingsModel = { pageSize: 50, pageSizes: [50, 100, 200] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };

  private destroyRef = inject(DestroyRef);

  constructor(
    private errorService: ErrorService,
    private batchService: InventoryBatchService,
    private dialog: MatDialog,
    private tenantWordPipe: TenantWordsPipe
  ) { }

  ngOnInit() {
    this.setColumns();
    this.getData();
  }

  getData() {
    this.isLoading = true;
    this.batchService.getAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: batches => {
        this.allRows = (batches ?? []).map(batch => this.toRow(batch));
        this.applyFilters();
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  applyFilters() {
    const term = this.searchTerm.trim().toLowerCase();
    this.rows = term
      ? this.allRows.filter(row => [row.lotNumber, row.itemName, row.workOrderType, row.createdBy]
        .some(value => value.toLowerCase().includes(term)))
      : [...this.allRows];
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  onDataBound() {
    this.grid?.autoFitColumns();
  }

  onRowSelected(args: RowSelectEventArgs) {
    const row = args?.data as BatchRow;
    if (row) this.openActions(row);
  }

  openActions(row: BatchRow) {
    InventoryBatchActionsComponent.open(this.dialog, { inventoryBatch: row.batch, closeDialogOnActions: true })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
        this.grid?.clearSelection();
        if (result)
          this.getData();
      });
  }

  exportExcel() {
    this.grid?.excelExport();
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByRow(_: number, row: BatchRow): number | undefined {
    return row.id;
  }

  private toRow(batch: InventoryBatchDTO): BatchRow {
    const detail = batch.batchDetails?.[0];
    return {
      batch,
      id: batch.id,
      lotNumber: detail?.lotNumber ?? '',
      itemName: detail?.itemName ?? '',
      workOrderType: batch.workOrderType ?? '',
      workOrderTypeColor: batch.workOrderTypeColor,
      createdBy: batch.createdBy ?? '',
      createdDateObj: batch.createdDate ? new Date(batch.createdDate) : null,
      availableQuantity: detail?.availableQuantity
    };
  }

  private setColumns() {
    this.columns = [
      { field: 'lotNumber', headerText: 'Lot Number', autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'itemName', headerText: this.tenantWordPipe.transform('Product'), autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'workOrderType', headerText: 'Work Order Type', autoFit: true, template: this.typeTemplate },
      { field: 'createdBy', headerText: 'Created By', autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'createdDateObj', headerText: 'Created', autoFit: true, type: 'date', format: { type: 'date', format: 'MM/dd/yy' } },
      { field: 'availableQuantity', headerText: this.tenantWordPipe.transform('Available Qty'), autoFit: true, format: KW_QUANTITY_FORMAT, textAlign: 'Right' },
      { headerText: '', template: this.actionTemplate, width: 56, allowSorting: false, allowFiltering: false, textAlign: 'Center' }
    ];
  }
}
